import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button, Modal, Form, Row, Col,Offcanvas } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = `${backendUrl}/api/communication`;

function CommunicationDetails({ projectId }) {
    const location = useLocation();
    const [communications, setCommunications] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [errors, setErrors] = useState({});
    const WithCreatePermission = withPermission(({ children }) => children, ['communication-create']);
    const WithEditPermission = withPermission(({ children }) => children, ['communication-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['communication-delete']);

    const [formData, setFormData] = useState({
        subject: '',
        source: 'phone',
        comments: '',
        documents: '',
        project_id: projectId,

    });
    const [isEdit, setIsEdit] = useState(false);
    const [selectedCommunication, setSelectedContact] = useState(null);

    // Ref to track whether the API call has been made
    const didFetchRef = useRef(false);


    useEffect(() => {
        if (!didFetchRef.current) {
            fetchCommunications();
            didFetchRef.current = true;  // Mark the fetch as done
        }
    }, [projectId]); // Dependencies are projectId and pathname

    const fetchCommunications = async (page = 1) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
            const response = await axios.get(`${API_URL}?project_id=${projectId}&page=${page}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                skipLoader: true,

            });
            setCommunications(response.data.communications.data);
            setPageCount(response.data.communications.last_page);

        } catch (error) {
            console.error('Error fetching communications:', error);
        }
    };
    const handleDownload = async (documentId, documentTitle) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(` ${backendUrl}/api/communication/${documentId}`, {
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                withCredentials: true,
            });

            const contentDisposition = response.headers['content-disposition'];
            let fileName = documentTitle;

            // Extract file name from headers
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match) {
                    fileName = match[1];
                }
            }
            fileName = fileName.replace(/(_\d+)(?=\.\w+$)/, '');
            // Create blob and download
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading the file:', error);
        }
    };

    const getFileNameFromPath = (filePath) => {
        // Extract the file name by splitting the path and taking the last part
        const pathParts = filePath.split('/');
        return pathParts[pathParts.length - 1]; // Return the file name (last part of the path)
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Update the formData with the selected file under 'avatar' field
            setFormData((prevData) => ({
                ...prevData,
                documents: file,  // Ensure this is 'avatar' and not 'profile_picture'
            }));
        }
    };
    const handleAddContact = () => {
        setFormData({ subject: '', comments: '', source: 'phone', project_id: projectId, });
        setIsEdit(false);
        setSelectedContact(null);
        setErrors({});
        setShowModal(true);
    };

    const handleEditContact = (communication) => {
        setFormData(communication);
        setIsEdit(true);
        setSelectedContact(communication);
        setShowModal(true);
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setErrors({}); // Clear any residual error
    };
    const handleDelete = async (id) => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            await axios.delete(`${API_URL}/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });
            setCommunications(communications.filter(communication => communication.id !== id));
        } catch (error) {
            console.error('Error deleting communication:', error);
        }

    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prevErrors) => {
            const { [name]: removed, ...rest } = prevErrors; // Remove the key
            return rest;
        });
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchCommunications(newPage)
    };
    useEffect(() => {
        if (!showModal) {
            setErrors({});
        }
    }, [showModal]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.subject) newErrors.subject = 'Subject is required';


        return newErrors;
    };
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return; // Stop here if validation fails
        }
        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const formDataToSend = new FormData();
            formDataToSend.append('subject', formData.subject);
            formDataToSend.append('comments', formData.comments);
            formDataToSend.append('source', formData.source);
            formDataToSend.append('project_id', projectId);

            if (formData.documents) {
                formDataToSend.append('documents', formData.documents);
            }

            let response;
            if (isEdit) {
                response = await axios.post(
                    `${API_URL}/${selectedCommunication.id}?_method=PUT`,
                    formDataToSend,
                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data',
                        'Tenant-Origin': origin,  // Include the Tenant-Origin header
                     } }
                );
                setCommunications(
                    communications.map((communication) =>
                        communication.id === selectedCommunication.id ? response.data.communication : communication
                    )
                );
            } else {
                response = await axios.post(
                    API_URL,
                    formDataToSend,
                    { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data',
                    'Tenant-Origin': origin,
                } }
                );
                setCommunications([...communications, response.data.communication]);
            }

            setErrors({});
            setShowModal(false);
        } catch (error) {
            {
                console.error("Unexpected error: ", error);
            }
        }
    };

    return (
        <div>
            <WithCreatePermission>
            <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "30px",
                                                        height: "30px",
                                                        padding: "5px",
                                                        backgroundColor: "ffffff",
                                                        borderColor: "#ccc",
                                                        color: "#333",
                                                        float: "right", marginBottom: "10px" 
                                                    }}
                                                    onClick={handleAddContact}                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    id="tool"
                                                    title="Add Communication"
                                                >
                                                    <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                                                </button>
            </WithCreatePermission><br/><br/>
            <table className="table table-hover datatable dt-responsive nowrap view-table font-size-13" >
            <thead>
                    <tr>          
                        <th scope="col">#</th>

                        <th scope="col">Subject</th>
                        <th scope="col">Source</th>

                        <th scope="col">Comment</th>
                        <th scope="col">Document</th>

                        <th scope="col">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {communications.map((communication,index) => (
                        <tr key={communication.id}>
                            <td>{index+1}</td>

                            <td>{communication.subject}</td>
                            <td>{communication.source}</td>

                            <td>{communication.comments}</td>
                            <td>
                                {communication.documents ? (
                                    <button
                                        className="btn btn-link p-0"
                                        style={{ fontSize: "13px", textDecoration: "none", color: "black", cursor: "pointer" }}
                                        onClick={() => handleDownload(communication.id, communication.documents)}
                                    >
                                        {getFileNameFromPath(communication.documents)}
                                    </button>
                                ) : (
                                    ' '
                                )}
                            </td>


                            <td>
                                <div className="dropdown">
                                    <i
                                        className="mdi mdi-dots-horizontal font-size-18"
                                        data-bs-toggle="dropdown"
                                        aria-expanded="false"
                                        style={{ cursor: 'pointer' }}
                                    ></i>
                                    <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                        <li><WithEditPermission>
                                            <button className="dropdown-item" onClick={() => handleEditContact(communication)}>
                                                Edit
                                            </button></WithEditPermission>
                                        </li>
                                        <li><WithDeletePermission>
                                            <button className="dropdown-item" onClick={() => handleDelete(communication.id)}>
                                                Delete
                                            </button></WithDeletePermission>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="d-flex justify-content-end pagination-sm">
            <ReactPaginate
                            previousLabel={'<'}
                            nextLabel={'>'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item previous'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item next'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                        />
            </div>
            {/* Modal for Add/Edit Contact */}
            {showModal && (
    <div
        className="custom-backdrop"
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.25)', // light dim (Bootstrap style)
            zIndex: 1059, // between parent & child offcanvas
        }}
        onClick={handleCloseModal}
    />
)}

            <Offcanvas show={showModal} onHide={handleCloseModal} placement='end' style={{ width: '500px' }}> 
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>{isEdit ? 'Edit Communication' : 'Add Communication'}</Offcanvas.Title>
                </Offcanvas.Header>
                <hr style={{ margin: 0 }} /><br/>
                <Offcanvas.Body>
                    <Form>

                        <Form.Group controlId="communicationName" className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Subject</Form.Label>
                            <Form.Control
                                style={{ fontSize: "13px" }}
                                type="text"
                                name="subject"
                                value={formData.subject}
                                className={`form-control ${errors?.subject ? 'is-invalid' : ''}`}
                                onChange={handleInputChange}
                            />
                            {errors.subject && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.subject}</div>}

                        </Form.Group>


                        <Form.Group controlId="communicationEmail" className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Comment</Form.Label>
                            <Form.Control style={{ fontSize: "13px" }} as="textarea" rows={3} name="comments" value={formData.comments} onChange={handleInputChange} />
                        </Form.Group>
                        <Form.Group controlId="source" className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Source</Form.Label>
                            <Form.Select
                                style={{ fontSize: "13px" }}
                                name="source"
                                value={formData.source}
                                onChange={handleInputChange}
                            >
                                <option value="phone">Phone</option>
                                <option value="chat">Chat</option>
                                <option value="whatsapp">Whatsapp</option>
                                <option value="email">Email</option>
                                <option value="other">Other</option>

                            </Form.Select>
                        </Form.Group>
                        <Form.Group controlId="documents" className='mb-3'>
                            <Form.Label style={{ fontSize: "13px" }}>Documents</Form.Label>
                            <Form.Control style={{ fontSize: "13px" }} type="file" name="documents" onChange={handleFileChange} />
                        </Form.Group>


                    </Form>
                </Offcanvas.Body>
                <div className="p-3 d-flex justify-content-end border-top">
                <Button variant="secondary me-2" onClick={() => setShowModal(false)}>Close</Button>
                    <Button variant="primary" onClick={handleSubmit}>Save</Button>
                </div>
            </Offcanvas>
        </div>
    );
}

export default CommunicationDetails;


